import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { Select } from '@mantine/core';
import { supabase } from '../supabase/client';
import { showNotification } from '@mantine/notifications';
import { AlertTriangle, BookOpen, CheckCircle, ChevronDown, Clock, FileText, Filter, PauseCircle, PlayCircle, Search } from 'lucide-react';
import { Event, formatDate, getCurrentEvent, minutesBetweenDates } from '../utils';
import LoadingComponent from '../components/Loading';
import ErrorComponent from '../components/ErrorComponent';
import { sortBy } from 'lodash';

const fetchTests = async (userId: string) => {
  const { data, error } = await supabase
    .from('profile_tests')
    .select(`
      id, started_on, expiration_time, completed_on, last_activity, score, sections,
      test:tests (*)
    `)
    .eq('profile_id', userId);
  if (error) throw error;
  return data;
};

const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState<string | null>(null);

  const { data: tests, isLoading, isError, error } = useQuery({
    queryKey: ['tests', user?.id],
    queryFn: () => fetchTests(user?.id ?? ''),
    enabled: !!user?.id,
  });

  if (isError) {
    return <div>Error loading tests: {(error as Error).message}</div>;
  }

  const getStatus = (test: any) => {
    if (!test.expiration_time) {
      return 'available';
    }

    if (!!test.started_on && !!test?.expiration_time && new Date() < new Date(test?.expiration_time)) {
      return 'in_progress';
    }

    if (!!test.completed_on) {
      return 'completed';
    }

    if (!!test?.expiration_time && new Date() >= new Date(test?.expiration_time)) {
      return 'expired';
    }

    return 'all';
  }

  const filteredTests = tests?.filter(test => {
    const matchesSearch = test.test.test_name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         test.test?.level?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const s = getStatus(test);
    const tab = (!activeTab || activeTab === 'all') || (activeTab && s === activeTab);
    const diff = !filterDifficulty || (filterDifficulty === test.test.level);

    return matchesSearch && tab && diff;
  }) ?? [];

  const availableTests = sortBy(filteredTests.filter(test => getStatus(test) === 'available'), 'started_on').reverse();
  const inProgressTests = sortBy(filteredTests.filter(test => getStatus(test) === 'in_progress'), 'started_on').reverse();
  const completedTests = sortBy(filteredTests.filter(test => ['completed', 'expired'].includes(getStatus(test))), 'started_on').reverse();

  // Format time from minutes to hours and minutes
  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours > 0 ? hours + 'h ' : ''}${mins}m`;
  };

  // Get status badge data based on test status
  const getStatusBadge = (test: any) => {
    switch (getStatus(test)) {
      case 'available':
        return { color: 'bg-emerald-100 text-emerald-800', text: 'Available' };
      case 'in_progress':
        return { color: 'bg-blue-100 text-blue-800', text: 'In Progress' };
      case 'completed':
        return { color: 'bg-purple-100 text-purple-800', text: 'Completed' };
      case 'expired':
        return { color: 'bg-gray-100 text-gray-800', text: 'Expired' };
      default:
        return { color: 'bg-gray-100 text-gray-800', text: 'Unknown' };
    }
  };

  const getActionButton = (test: any) => {
    switch (getStatus(test)) {
      case 'available':
        return (
          <button
            className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center transition duration-150"
            onClick={async () => {
              const { error } = await supabase.rpc('start_test', {
                _test_id: test.id,
              });

              if (error) {
                showNotification({
                  title: 'Alert',
                  message: error.message,
                });
                return;
              }

              navigate(`/test/${test.id}`);
            }}
          >
            <PlayCircle size={18} className="mr-2" />
            Start Test
          </button>
        );
      case 'in_progress':
        return (
          <button
            className="w-full mt-4 bg-emerald-600 hover:bg-emerald-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center transition duration-150"
            onClick={async () => {    
              navigate(`/test/${test.id}`);
            }}
          >
            <PauseCircle size={18} className="mr-2" />
            Continue Test
          </button>
        );
      case 'completed':
        return (
          <button onClick={() => navigate(`/tests/${test.id}`)} className="w-full mt-4 bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 px-4 rounded-lg flex items-center justify-center transition duration-150">
            <FileText size={18} className="mr-2" />
            View Results
          </button>
        );
      case 'expired':
        return (
          <button className="w-full mt-4 bg-gray-300 text-gray-600 font-medium py-2 px-4 rounded-lg flex items-center justify-center cursor-not-allowed" disabled>
            <AlertTriangle size={18} className="mr-2" />
            Test Expired
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero section */}
      <div className="py-12 px-4 sm:px-6 lg:px-8 bg-linear-to-r from-indigo-600 to-purple-600 mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="md:flex md:items-center md:justify-between">
            <div className="md:w-2/3">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Test Library</h1>
              <p className="text-xl text-indigo-100 mb-6">
                Browse, start and continue your LSAT practice tests to improve your score
              </p>
              <div className="flex space-x-4">
                <button 
                  className="px-6 py-3 bg-white text-indigo-600 rounded-lg font-medium hover:bg-indigo-50 transition-colors shadow-md flex items-center"
                  onClick={() => navigate('/order')}
                >
                  <BookOpen className="mr-2 h-5 w-5" />
                  Order New Tests
                </button>
              </div>
            </div>
            <div className="md:w-1/3 mt-8 md:mt-0">
              <div className="bg-white/10 rounded-xl p-4 border border-indigo-300/30">
                <div className="flex items-center mb-3">
                  <FileText className="h-5 w-5 text-indigo-100 mr-2" />
                  <h3 className="text-white font-medium">Test Stats</h3>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-indigo-100 text-sm">Available</p>
                    <p className="text-white font-bold text-2xl">{availableTests.length}</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">In Progress</p>
                    <p className="text-white font-bold text-2xl">{inProgressTests.length}</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Completed</p>
                    <p className="text-white font-bold text-2xl">{completedTests?.filter(t => !!t.completed_on).length}</p>
                  </div>
                  <div>
                    <p className="text-indigo-100 text-sm">Total Tests</p>
                    <p className="text-white font-bold text-2xl">{tests?.length ?? 0}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {
        isLoading &&
          <LoadingComponent heading='Preparing Your Exams' subheading='Please wait while we retrieve your information' />
      }
      {
        isError &&
          <ErrorComponent />
      }
      {
        !isLoading && !isError && <div className="p-6 pt-0">
          {/* Search and filter bar */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex flex-wrap items-center gap-4">
            <div className="flex flex-1 items-center bg-gray-100 rounded-lg px-3 py-2">
              <Search size={18} className="text-gray-500 mr-2" />
              <input 
                type="text" 
                placeholder="Search tests..." 
                className="bg-transparent border-none focus:outline-none flex-1 text-gray-700"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="flex items-center gap-2">
              <div className="flex items-center">
                <Select onChange={v => setFilterDifficulty(v)} placeholder='Difficulty' data={[
                  { value: "beginner", label: "Beginner"},
                  { value: "intermediate", label: "Intermediate" },
                  { value: "expert", label: "Expert" }
                ]}
                clearable
                leftSection={
                  <Filter size={16} className="text-gray-500 mr-2" />
                } />
              </div>
            </div>
          </div>

          {/* Tab navigation */}
          <div className="bg-white rounded-lg shadow-sm mb-6">
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px">
                <button
                  onClick={() => setActiveTab('all')}
                  className={`py-4 px-6 font-medium text-sm border-b-2 ${
                    activeTab === 'all'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  All Tests
                </button>
                <button
                  onClick={() => setActiveTab('available')}
                  className={`py-4 px-6 font-medium text-sm border-b-2 ${
                    activeTab === 'available'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Available
                </button>
                <button
                  onClick={() => setActiveTab('in_progress')}
                  className={`py-4 px-6 font-medium text-sm border-b-2 ${
                    activeTab === 'in_progress'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  In Progress
                </button>
                <button
                  onClick={() => setActiveTab('completed')}
                  className={`py-4 px-6 font-medium text-sm border-b-2 ${
                    activeTab === 'completed'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Completed
                </button>
              </nav>
            </div>
          </div>

          {/* Test lists */}
          <div className="space-y-8">
            {/* Available Tests Section */}
            {(activeTab === 'all' || activeTab === 'available') && availableTests.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <PlayCircle size={20} className="text-emerald-500 mr-2" />
                  Available Tests
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableTests.map(test => {
                    return (
                      <div key={`avail-${test.id}`} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow duration-200">
                        <div className="p-5">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800">{test.test.test_name}</h3>
                              <p className="text-gray-600 text-sm mt-1">{test.test.description}</p>
                            </div>
                            <span className={`${getStatusBadge(test).color} text-xs px-2 py-1 rounded-full font-medium`}>
                              {getStatusBadge(test).text}
                            </span>
                          </div>
                          
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <p className="text-xs text-gray-500">Duration</p>
                                <p className="text-sm font-medium text-gray-700">{formatTime(((test.sections as unknown as Event[]) ?? [])?.reduce((p, c) => c.duration+p, 0))}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Sections</p>
                                <p className="text-sm font-medium text-gray-700">{((test.sections as unknown as Event[]) ?? [])?.filter(s => s.type === "section").length ?? 0} ({((test.sections as unknown as Event[]) ?? [])?.filter(s => s.type === "break").length ?? 0} breaks)</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Type</p>
                                <p className="text-sm font-medium text-gray-700">{test.test.category}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Difficulty</p>
                                <p className="text-sm font-medium text-gray-700 capitalize">{test.test.level}</p>
                              </div>
                            </div>
                          </div>
                          
                          {getActionButton(test)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* In Progress Tests Section */}
            {(activeTab === 'all' || activeTab === 'in_progress') && inProgressTests.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <PauseCircle size={20} className="text-blue-500 mr-2" />
                  In Progress Tests
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {inProgressTests.map(test => {
                    const currentEvent = getCurrentEvent(test.started_on ?? '', test.test.sections as unknown as Event[])
                    const { section } = currentEvent ?? {};

                    return (
                      <div key={`prog-${test.id}`} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow duration-200">
                        <div className="bg-blue-50 p-3 flex justify-between items-center">
                          <div className="flex items-center">
                            <Clock size={16} className="text-blue-500 mr-2" />
                            <span className="text-blue-700 text-sm font-medium">
                              {test.expiration_time && formatTime(minutesBetweenDates(new Date(), test.expiration_time))} remaining
                            </span>
                          </div>
                          <span className="text-blue-700 text-sm font-medium">
                            Section {section ?? 1}/{(test.sections as unknown as Event[])?.filter(x => x.type === "section").length ?? 0}
                          </span>
                        </div>
                        
                        <div className="p-5">
                          <div className="flex justify-between items-start">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800">{test.test.test_name}</h3>
                              <p className="text-gray-600 text-sm mt-1">{test.test.description}</p>
                            </div>
                            <span className={`${getStatusBadge(test).color} text-xs px-2 py-1 rounded-full font-medium`}>
                              {getStatusBadge(test).text}
                            </span>
                          </div>
                          
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <p className="text-xs text-gray-500">Duration</p>
                                <p className="text-sm font-medium text-gray-700">{formatTime((test.sections as unknown as Event[] ?? [])?.reduce((p, c) => c.duration+p, 0))}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Last Activity</p>
                                <p className="text-sm font-medium text-gray-700">{test.last_activity}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Type</p>
                                <p className="text-sm font-medium text-gray-700">{test.test.description}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Difficulty</p>
                                <p className="text-sm font-medium text-gray-700 capitalize">{test.test.level}</p>
                              </div>
                            </div>
                          </div>
                          
                          {getActionButton(test)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Completed Tests Section */}
            {(activeTab === 'all' || activeTab === 'completed') && completedTests.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                  <CheckCircle size={20} className="text-purple-500 mr-2" />
                  Completed Tests
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {completedTests.map(test => (
                    <div key={`completed-${test.id}`} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 hover:shadow-md transition-shadow duration-200">
                      {getStatus(test) === 'completed' && test.completed_on && (
                        <div className="bg-purple-50 p-3 flex justify-between items-center">
                          <div className="flex items-center">
                            <FileText size={16} className="text-purple-500 mr-2" />
                            <span className="text-purple-700 text-sm font-medium">
                              Completed on {formatDate(test.completed_on)}
                            </span>
                          </div>
                          <span className="text-purple-700 text-sm font-medium flex items-center">
                            Score: <span className="font-bold ml-1">{test.score}</span>
                          </span>
                        </div>
                      )}
                      
                      {getStatus(test) === 'expired' && (
                        <div className="bg-gray-50 p-3 flex justify-between items-center">
                          <div className="flex items-center">
                            <AlertTriangle size={16} className="text-gray-500 mr-2" />
                            <span className="text-gray-700 text-sm font-medium">
                              This test has expired
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="p-5">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-800">{test.test.test_name}</h3>
                            <p className="text-gray-600 text-sm mt-1">{test.test.description}</p>
                          </div>
                          <span className={`${getStatusBadge(test).color} text-xs px-2 py-1 rounded-full font-medium`}>
                            {getStatusBadge(test).text}
                          </span>
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs text-gray-500">Type</p>
                              <p className="text-sm font-medium text-gray-700">{test.test.description}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Sections</p>
                              <p className="text-sm font-medium text-gray-700">{(test.sections as unknown as Event[])?.filter(x => x.type === "section").length ?? 0}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Difficulty</p>
                              <p className="text-sm font-medium text-gray-700 capitalize">{test.test.level}</p>
                            </div>
                            {test.started_on && (
                              <div>
                                <p className="text-xs text-gray-500">Started On</p>
                                <p className="text-sm font-medium text-gray-700">{formatDate(test.started_on)}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {getActionButton(test)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No tests found message */}
            {filteredTests.length === 0 && (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-blue-50">
                  <Search size={24} className="text-blue-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-800 mb-2">No tests found</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  We couldn't find any tests matching your current filters. Try adjusting your search criteria or <Link to="/order" className="text-blue-600 hover:text-blue-500 decoration-2 hover:underline focus:outline-none focus:underline opacity-90">order new tests</Link>.
                </p>
              </div>
            )}
          </div>
        </div>
      }
    </div>
  );
};

export default DashboardPage;
